import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { PREDECESSOR_RELATED_FIELD_KEYS, Task } from '@tasks/tasks.types';
import { TaskUtils } from '@tasks/tasks.utils';

export type TaskChangeEventSource = 'api-diff' | 'websocket';

export interface TaskChangeEvent {
  taskId: number;
  taskListId: number;
  changedFields: Record<string, unknown>;
  previousValues: Record<string, unknown>;
  fullTask: Task;
  source: TaskChangeEventSource;
}

@Injectable({
  providedIn: 'root',
})
export class PretendTaskChangePusher {
  private readonly taskChangeSubject = new Subject<TaskChangeEvent>();

  readonly taskChange$: Observable<TaskChangeEvent> = this.taskChangeSubject.asObservable();

  emit(event: TaskChangeEvent): void {
    this.taskChangeSubject.next(event);
  }

  /**
   * Iterates over API response tasks, diffs each against current state (visible + resync fields),
   * and emits one TaskChangeEvent per task that has changes. Used for cascading updates
   * so the grid can apply surgical row updates with flash instead of full refresh.
   */
  diffAndEmit(apiTasks: Task[], currentTasks: Task[], visibleFields: string[]): void {
    const syncSet = new Set([...visibleFields, ...TaskUtils.FIELDS_THAT_REQUIRE_BACKEND_RESYNC]);
    const flatApiTasks = TaskUtils.flattenTasks(apiTasks ?? []);

    for (const apiTask of flatApiTasks) {
      const currentTask = TaskUtils.deepFindTaskById(currentTasks, apiTask.id);
      if (!currentTask) {
        continue;
      }

      const allDiffKeys = TaskUtils.getDiffKeys(
        currentTask as Record<string, unknown>,
        apiTask as Record<string, unknown>,
      );
      const matchedDiffKeys = allDiffKeys.filter((k) => syncSet.has(k));
      if (matchedDiffKeys.length === 0) {
        continue;
      }

      const changedFields = matchedDiffKeys.reduce<Record<string, unknown>>(
        (acc, key) => ({ ...acc, [key]: apiTask[key as keyof Task] }),
        {},
      );
      const previousValues = matchedDiffKeys.reduce<Record<string, unknown>>(
        (acc, key) => ({ ...acc, [key]: currentTask[key as keyof Task] }),
        {},
      );

      if (matchedDiffKeys.some((k) => PREDECESSOR_RELATED_FIELD_KEYS.has(k))) {
        if (!('start_date' in changedFields)) {
          changedFields['start_date'] = apiTask.start_date;
          previousValues['start_date'] = currentTask.start_date;
        }
        if (!('due_date' in changedFields)) {
          changedFields['due_date'] = apiTask.due_date;
          previousValues['due_date'] = currentTask.due_date;
        }
      }

      this.emit({
        taskId: apiTask.id,
        taskListId: apiTask.task_list_id ?? 0,
        changedFields,
        previousValues,
        fullTask: apiTask,
        source: 'api-diff',
      });
    }
  }
}
